'use client'

import * as React from 'react'
import { Button } from '@repo/studio-design-system-ui-button'
import { tw } from 'utils/classnames'
import { LoopingCarousel } from '@repo/studio-components-looping-carousel/looping-carousel'
import type { ScrollButtonRenderProps } from '@repo/studio-components-looping-carousel/looping-carousel'
import { PromoCard } from '@repo/studio-components-promo-card/promo-card'
import type { ButtonSize, ButtonVariant } from '@repo/studio-design-system-ui-button'

export interface GptImageBannerCopy {
  title: React.ReactNode
  description: React.ReactNode
  button: React.ReactNode
}

export interface GptImageBannerLinkProps {
  children: React.ReactNode
}

export interface GptImageBannerImageProps {
  src: string
  className: string
}

export type GptImageBannerLinkRenderer = (props: GptImageBannerLinkProps) => React.ReactElement
export type GptImageBannerImageRenderer = (props: GptImageBannerImageProps) => React.ReactNode

export interface GptImageBannerCarouselConfig {
  className: string
  itemWidth: number
  height: number
  buttonHeight: number
}

interface GptImageItem {
  src: string
}

interface InternalGptImageItem extends GptImageItem {
  imageClassName?: string
  renderImage: GptImageBannerImageRenderer
  renderLink: GptImageBannerLinkRenderer
}

export interface GptImageBannerProps {
  lfsUrl: string
  /** Copy for the default promo card. Ignored when `promoCard` is provided. */
  copy?: GptImageBannerCopy
  /** Overrides the left promo card while keeping the gallery on the right unchanged. */
  promoCard?: React.ReactNode
  renderLink: GptImageBannerLinkRenderer
  renderImage?: GptImageBannerImageRenderer
  className?: string
  layoutClassName?: string
  promoCardClassName?: string
  imageClassName?: string
  gap?: number
  shadowColor?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  renderCarouselButton?: (props: ScrollButtonRenderProps) => React.ReactNode
  mobileCarouselConfig?: Partial<GptImageBannerCarouselConfig>
  desktopCarouselConfig?: Partial<GptImageBannerCarouselConfig>
}

export const GPT_IMAGE_GALLERY_PATHS = [
  '/studio/homepage/gpt-gallery/1_450x450.webp',
  '/studio/homepage/gpt-gallery/2_450x450.webp',
  '/studio/homepage/gpt-gallery/3_450x450.webp',
  '/studio/homepage/gpt-gallery/4_450x450.webp',
  '/studio/homepage/gpt-gallery/5_257x450.webp',
  '/studio/homepage/gpt-gallery/6_450x450.webp',
  '/studio/homepage/gpt-gallery/7_450x450.webp',
  '/studio/homepage/gpt-gallery/8_450x450.webp',
  '/studio/homepage/gpt-gallery/9_450x450.webp',
  '/studio/homepage/gpt-gallery/10_336x450.webp',
] as const

const DEFAULT_LAYOUT_CLASSNAME = 'grid grid-cols-1 gap-1 @xl:grid-cols-[380px_1fr] items-center'

const DEFAULT_MOBILE_CAROUSEL_CONFIG: GptImageBannerCarouselConfig = {
  className: '@4xl:hidden h-full',
  itemWidth: 200,
  height: 300,
  buttonHeight: 300,
}

const DEFAULT_DESKTOP_CAROUSEL_CONFIG: GptImageBannerCarouselConfig = {
  className: 'hidden @4xl:flex h-full items-center',
  itemWidth: 232,
  height: 348,
  buttonHeight: 348,
}

const NOOP = () => {}

export function getGptImageGalleryImages(lfsUrl: string): GptImageItem[] {
  const normalizedLfsUrl = lfsUrl.replace(/\/$/, '')

  return GPT_IMAGE_GALLERY_PATHS.map(path => ({
    src: `${normalizedLfsUrl}${path}`,
  }))
}

function defaultRenderImage({ src, className }: GptImageBannerImageProps) {
  return <img src={src} alt="" className={className} loading="lazy" />
}

function GptImageCarouselItem({ item }: { item: InternalGptImageItem; realIndex: number }) {
  return (
    <div className="relative size-full overflow-hidden rounded-2">
      {item.renderLink({
        children: item.renderImage({
          src: item.src,
          className: tw('size-full bg-gray-800 object-cover', item.imageClassName),
        }),
      })}
    </div>
  )
}

function GptImagePromoCard({
  copy,
  promoCardClassName,
  renderLink,
}: {
  copy: GptImageBannerCopy
  promoCardClassName?: string
  renderLink: GptImageBannerLinkRenderer
}) {
  return (
    <PromoCard.Root className={tw('bg-gray-900 @container', promoCardClassName)}>
      {/* eslint-disable-next-line cf-web/require-icon-parent */}
      <ChatGptBackgroundIcon className="absolute text-gray-300 inset-0 size-full" />
      <PromoCard.Logo>
        <ChatGptLogo />
      </PromoCard.Logo>
      <PromoCard.Title>{copy.title}</PromoCard.Title>
      <PromoCard.Description>{copy.description}</PromoCard.Description>
      <Button variant="overlay" size="md" asChild className="isolate self-start text-center">
        {renderLink({ children: copy.button })}
      </Button>
    </PromoCard.Root>
  )
}

export function GptImageBanner({
  lfsUrl,
  copy,
  promoCard,
  renderLink,
  renderImage = defaultRenderImage,
  className,
  layoutClassName = DEFAULT_LAYOUT_CLASSNAME,
  promoCardClassName,
  imageClassName,
  gap = 8,
  shadowColor = 'text-theme_semantic_surface_scrim_edge_base-start w-12',
  buttonVariant,
  buttonSize,
  renderCarouselButton,
  mobileCarouselConfig,
  desktopCarouselConfig,
}: GptImageBannerProps) {
  const galleryImages = React.useMemo(
    () =>
      getGptImageGalleryImages(lfsUrl).map(item => ({
        ...item,
        imageClassName,
        renderImage,
        renderLink,
      })),
    [imageClassName, lfsUrl, renderImage, renderLink],
  )

  const mobileCarousel = {
    ...DEFAULT_MOBILE_CAROUSEL_CONFIG,
    ...mobileCarouselConfig,
  }
  const desktopCarousel = {
    ...DEFAULT_DESKTOP_CAROUSEL_CONFIG,
    ...desktopCarouselConfig,
  }

  return (
    <div className={tw('@container', className)}>
      <div className={layoutClassName}>
        {promoCard ??
          (copy ? (
            <GptImagePromoCard
              copy={copy}
              promoCardClassName={promoCardClassName}
              renderLink={renderLink}
            />
          ) : null)}
        <div className="relative overflow-hidden">
          <LoopingCarousel
            className={mobileCarousel.className}
            shadowColor={shadowColor}
            items={galleryImages}
            ItemComponent={GptImageCarouselItem}
            itemWidth={mobileCarousel.itemWidth}
            height={mobileCarousel.height}
            buttonHeight={mobileCarousel.buttonHeight}
            gap={gap}
            hasNextPage={false}
            isFetchingNextPage={false}
            fetchNextPage={NOOP}
            buttonVariant={buttonVariant}
            buttonSize={buttonSize}
            renderScrollButton={renderCarouselButton}
          />
          <LoopingCarousel
            className={desktopCarousel.className}
            shadowColor={shadowColor}
            items={galleryImages}
            ItemComponent={GptImageCarouselItem}
            itemWidth={desktopCarousel.itemWidth}
            height={desktopCarousel.height}
            buttonHeight={desktopCarousel.buttonHeight}
            gap={gap}
            hasNextPage={false}
            isFetchingNextPage={false}
            fetchNextPage={NOOP}
            buttonVariant={buttonVariant}
            buttonSize={buttonSize}
            renderScrollButton={renderCarouselButton}
          />
        </div>
      </div>
    </div>
  )
}

function ChatGptLogo() {
  return (
    <svg
      width="106"
      height="26"
      viewBox="0 0 106 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#chatgpt-logo-icon-clip)">
        <path
          d="M24.23 10.62C24.83 8.85 24.62 6.92 23.67 5.31C22.23 2.84 19.35 1.58 16.53 2.17C15.28 0.78 13.48 -0.01 11.59 0C8.71 -0.01 6.16 1.82 5.27 4.53C3.42 4.9 1.83 6.04 0.89 7.66C-0.55 10.12 -0.22 13.22 1.71 15.32C1.11 17.09 1.32 19.03 2.27 20.63C3.71 23.1 6.59 24.36 9.41 23.77C10.66 25.16 12.46 25.95 14.35 25.94C17.23 25.95 19.78 24.12 20.67 21.41C22.52 21.04 24.11 19.9 25.05 18.28C26.49 15.82 26.16 12.72 24.23 10.62L24.23 10.62ZM14.35 24.24C13.2 24.25 12.08 23.85 11.19 23.12C11.23 23.1 11.3 23.06 11.35 23.03L16.58 20.05C16.85 19.9 17.02 19.62 17.01 19.31V12.03L19.23 13.29C19.25 13.3 19.27 13.33 19.27 13.35V19.38C19.27 22.07 17.07 24.24 14.35 24.24ZM3.76 19.78C3.19 18.8 2.98 17.65 3.18 16.53C3.21 16.55 3.28 16.59 3.33 16.62L8.57 19.6C8.83 19.75 9.16 19.75 9.43 19.6L15.82 15.96V18.48C15.82 18.51 15.81 18.53 15.79 18.55L10.49 21.56C8.14 22.9 5.13 22.11 3.76 19.78H3.76ZM2.39 8.51C2.96 7.52 3.87 6.77 4.95 6.38C4.95 6.42 4.95 6.5 4.95 6.56V12.52C4.95 12.83 5.11 13.11 5.38 13.26L11.77 16.9L9.56 18.16C9.53 18.17 9.51 18.18 9.48 18.17L4.19 15.15C1.84 13.8 1.03 10.83 2.38 8.51L2.39 8.51ZM20.56 12.68L14.17 9.04L16.38 7.78C16.41 7.77 16.43 7.76 16.46 7.78L21.75 10.79C24.11 12.13 24.92 15.11 23.55 17.43C22.98 18.42 22.07 19.17 20.99 19.56V13.42C20.99 13.11 20.83 12.83 20.56 12.68H20.56ZM22.76 9.41C22.73 9.39 22.66 9.35 22.61 9.32L17.37 6.34C17.11 6.18 16.78 6.18 16.51 6.34L10.12 9.98V7.46C10.12 7.43 10.13 7.41 10.15 7.39L15.45 4.38C17.8 3.04 20.82 3.84 22.18 6.16C22.75 7.14 22.96 8.29 22.76 9.41H22.76ZM8.92 13.91L6.71 12.65C6.68 12.63 6.67 12.61 6.67 12.59V6.56C6.67 3.87 8.87 1.69 11.6 1.7C12.75 1.7 13.86 2.09 14.74 2.82C14.7 2.84 14.64 2.88 14.59 2.91L9.35 5.89C9.09 6.04 8.92 6.32 8.92 6.63L8.92 13.9V13.91ZM10.12 11.35L12.97 9.73L15.82 11.35V14.59L12.97 16.21L10.12 14.59V11.35Z"
          fill="currentColor"
        />
      </g>
      <g transform="translate(32 6.5)">
        <path
          d="M5.57 0C8.21 -0.07 10.3 1.07 10.92 3.7L8.41 4.31C8.18 3.58 7.78 2.86 7.07 2.51C6.21 2.09 5.21 2.09 4.33 2.56C2.43 3.56 2.43 6.65 2.95 8.66C3.19 9.59 3.89 10.3 4.61 10.56C5.61 10.92 6.61 10.82 7.38 10.14L7.39 10.15C8 9.61 8.23 8.95 8.45 8.11L10.96 8.85C10.27 11.5 8.67 12.88 5.88 12.98C3.27 13.08 1.01 11.48 0.32 8.94C-0.14 7.26 -0.1 5.51 0.41 3.84C1.12 1.52 3.17 0.07 5.57 0Z"
          fill="currentColor"
        />
        <path
          d="M15.49 4.73C16.78 3.24 18.95 3 20.44 4.22C21.09 4.75 21.31 5.68 21.31 6.51L21.34 12.77L18.88 12.76L18.86 6.85C18.86 6.45 18.71 5.99 18.47 5.73C18.23 5.48 17.78 5.34 17.39 5.34C16.48 5.35 15.69 5.9 15.6 6.81L15.47 8.07V12.76L13 12.76L13 0.23L15.46 0.22L15.49 4.73Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M26.09 3.53C27.81 3.34 30.24 3.4 30.87 4.96C31.2 5.78 31.09 6.7 31.09 7.57L31.1 10.63C31.1 11.38 31.33 12.08 31.64 12.76L29.22 12.78L28.89 11.85L28.88 11.85C27.66 12.99 25.86 13.35 24.34 12.53C23.57 12.12 23.14 11.31 23.08 10.56C22.94 8.72 24.05 7.94 25.79 7.54L28.69 6.88C28.73 6.42 28.7 6.05 28.45 5.73C28.25 5.47 27.75 5.38 27.37 5.35C26.49 5.27 25.77 5.57 25.54 6.51L23.34 6.11C23.57 4.73 24.66 3.69 26.09 3.53ZM26.38 9.03C25.92 9.14 25.55 9.55 25.53 9.96C25.52 10.19 25.58 10.42 25.7 10.62C25.74 10.7 25.8 10.77 25.87 10.84C25.94 10.91 26.02 10.97 26.11 11.02C26.13 11.03 26.14 11.04 26.16 11.05C27.05 11.51 28.37 10.99 28.62 9.92V9.93C28.74 9.43 28.7 8.95 28.7 8.46L28.7 8.46L26.38 9.03Z"
          fill="currentColor"
        />
        <path
          d="M36.03 3.65L37.64 3.67V5.63L36.01 5.63L36.02 10.42C36.02 10.81 36.49 10.98 36.8 10.91L37.62 10.71L37.82 12.61L37.82 12.61C36.58 13.09 34.79 13.16 34.04 12.29C33.68 11.87 33.54 11.21 33.53 10.64L33.52 5.64L32.43 5.63L32.43 3.65L33.53 3.65L33.51 1.86L35.99 0.41L36.03 3.65Z"
          fill="currentColor"
        />
        <path
          d="M44.47 0.02C45.41 -0.01 46.3 0.02 47.2 0.25C48.85 0.73 50.08 1.99 50.39 3.71L47.84 4.2C47.49 2.91 46.37 2.23 45.08 2.21C43.43 2.18 42.03 3.12 41.65 4.77C41.38 5.92 41.4 7.14 41.71 8.27C42.17 9.96 43.67 10.94 45.4 10.74C46.31 10.64 47.12 10.29 47.93 9.73L47.93 9.73L47.94 8.18H45.06L45.07 6L50.56 5.99L50.55 11.08C48.45 12.75 45.75 13.37 43.18 12.74C39.4 11.78 38.12 7.62 39.12 4.18C39.84 1.72 41.92 0.1 44.47 0.02Z"
          fill="currentColor"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M58.82 0.24C60.92 0.25 62.47 1.81 62.41 3.91C62.64 6.12 61.06 7.97 58.78 8L55.43 8.05L55.42 12.76L52.85 12.77V0.22L58.82 0.24ZM55.43 2.4H55.43L55.43 5.88L58.24 5.8L58.24 5.8C58.61 5.79 58.92 5.67 59.16 5.47C59.58 5.15 59.81 4.61 59.79 4.05C59.77 3.24 59.16 2.46 58.25 2.45L55.43 2.4V2.4Z"
          fill="currentColor"
        />
        <path
          d="M73.55 0.22L73.55 2.38L69.85 2.38L69.85 12.75L67.28 12.76V2.39L63.56 2.37L63.59 0.22L73.55 0.22Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="chatgpt-logo-icon-clip">
          <rect width="25.9401" height="25.9401" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

function ChatGptBackgroundIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 350 380" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity={0.7} filter="url(#gpt-bg-f0)">
        <ellipse
          cx={286.5}
          cy={324.5}
          rx={200.5}
          ry={204.5}
          transform="rotate(90 286.5 324.5)"
          fill="var(--500)"
        />
      </g>
      <g opacity={0.7} filter="url(#gpt-bg-f1)">
        <ellipse
          cx={90.5}
          cy={123.5}
          rx={200.5}
          ry={204.5}
          transform="rotate(90 90.5 123.5)"
          fill="var(--700)"
        />
      </g>
      <mask
        id="gpt-bg-mask"
        style={{ maskType: 'alpha' } as React.CSSProperties}
        maskUnits="userSpaceOnUse"
        x={-18}
        y={46}
        width={381}
        height={380}
      >
        <g clipPath="url(#gpt-bg-clip)">
          <path
            d="M337.44 201.53C346.18 175.66 343.17 147.31 329.2 123.77C308.19 87.67 265.94 69.1 224.69 77.84C206.34 57.44 179.97 45.84 152.32 46C110.15 45.91 72.74 72.7 59.76 112.29C32.67 117.76 9.29 134.5 -4.39 158.21C-25.56 194.22 -20.74 239.6 7.54 270.48C-1.19 296.36 1.82 324.7 15.79 348.24C36.8 384.34 79.04 402.91 120.3 394.17C138.64 414.57 165.02 426.18 192.66 426C234.86 426.11 272.29 399.29 285.26 359.66C312.35 354.19 335.73 337.46 349.42 313.74C370.56 277.74 365.72 232.38 337.45 201.51L337.44 201.53ZM192.69 401.17C175.8 401.19 159.45 395.36 146.49 384.68C147.08 384.38 148.1 383.82 148.76 383.41L225.45 339.71C229.37 337.52 231.78 333.39 231.75 328.94V222.27L264.16 240.73C264.51 240.9 264.74 241.23 264.79 241.61V329.95C264.74 369.23 232.5 401.08 192.69 401.17ZM37.63 335.82C29.17 321.4 26.13 304.5 29.03 288.1C29.59 288.44 30.59 289.04 31.3 289.44L107.99 333.14C111.87 335.39 116.69 335.39 120.59 333.14L214.21 279.8V316.73C214.23 317.11 214.05 317.48 213.75 317.72L136.23 361.88C101.7 381.5 57.61 369.84 37.64 335.82H37.63ZM17.45 170.64C25.87 156.2 39.17 145.16 55.01 139.42C55.01 140.08 54.97 141.23 54.97 142.04V229.45C54.95 233.89 57.36 238.01 61.27 240.21L154.89 293.54L122.48 312.01C122.15 312.22 121.74 312.26 121.38 312.1L43.85 267.9C9.4 248.21 -2.42 204.72 17.44 170.66L17.45 170.64ZM283.73 231.79L190.11 178.45L222.52 159.99C222.85 159.78 223.26 159.74 223.62 159.9L301.14 204.06C335.66 223.74 347.49 267.31 327.55 301.37C319.11 315.78 305.83 326.83 290 332.57V242.55C290.04 238.11 287.64 234 283.74 231.79H283.73ZM315.98 183.88C315.42 183.54 314.42 182.95 313.71 182.54L237.02 138.84C233.14 136.6 228.32 136.6 224.42 138.84L130.81 192.19V155.25C130.78 154.87 130.96 154.51 131.26 154.27L208.78 110.14C243.31 90.49 287.45 102.18 307.36 136.27C315.77 150.66 318.81 167.51 315.96 183.88H315.98ZM113.19 249.71L80.76 231.24C80.42 231.08 80.19 230.75 80.14 230.37V142.03C80.16 102.69 112.5 70.82 152.36 70.84C169.22 70.84 185.54 76.69 198.5 87.33C197.91 87.64 196.9 88.19 196.23 88.6L119.54 132.3C115.62 134.5 113.21 138.6 113.23 143.06L113.19 249.69V249.71ZM130.79 212.25L172.49 188.49L214.19 212.24V259.76L172.49 283.51L130.79 259.76V212.25Z"
            fill="var(--1000)"
          />
        </g>
      </mask>
      <g mask="url(#gpt-bg-mask)">
        <g opacity={0.9} filter="url(#gpt-bg-f2)">
          <ellipse
            cx={-63}
            cy={142}
            rx={181}
            ry={262.5}
            transform="rotate(90 -63 142)"
            fill="var(--500)"
          />
        </g>
        <g filter="url(#gpt-bg-f3)">
          <ellipse
            cx={313}
            cy={256.5}
            rx={113.5}
            ry={177.5}
            transform="rotate(90 313 256.5)"
            fill="var(--1000)"
          />
        </g>
      </g>
      <defs>
        <filter
          id="gpt-bg-f0"
          x={-208}
          y={-166}
          width={989}
          height={981}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation={145} />
        </filter>
        <filter
          id="gpt-bg-f1"
          x={-404}
          y={-367}
          width={989}
          height={981}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation={145} />
        </filter>
        <filter
          id="gpt-bg-f2"
          x={-544.16}
          y={-257.66}
          width={962.32}
          height={799.32}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation={109.33} />
        </filter>
        <filter
          id="gpt-bg-f3"
          x={-110.5}
          y={-103}
          width={847}
          height={719}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation={123} />
        </filter>
        <clipPath id="gpt-bg-clip">
          <rect width={380} height={380} fill="white" transform="translate(-17.5 46)" />
        </clipPath>
      </defs>
    </svg>
  )
}
